import type { FlowBuilderStreamEventReceivedPayload } from '../../types/flowBuilder/flowBuilderApi';
import type {
  FlowBuilderAgentState,
  FlowBuilderCreatedResource,
  FlowBuilderFlowPreview,
  FlowBuilderIssueGroup,
  FlowBuilderTurn,
  FlowBuilderTurnActivity,
} from '../../types/flowBuilder/flowBuilderState';
import type {
  FlowBuilderStreamEvent,
  FlowBuilderStreamEventOf,
  FlowBuilderValidationIssue,
} from '../../types/flowBuilder/flowBuilderStreamEvents';
import {
  flowBuilderToolLabel,
  humanizeFlowBuilderStatus,
} from '../../utils/flowBuilder/flowBuilderLabels';
import { isFlowBuilderTerminalEvent } from '../../utils/flowBuilder/flowBuilderStreamEvent';

/*
 * Tradução do stream do FlowBuilder para o estado do chat, um evento por vez (porte do
 * `streamEventReducer` do LIP). As funções mutam o rascunho do Immer que o slice entrega, então o
 * reducer continua puro visto de fora. O que no LIP eram "efeitos" (pintar o canvas, recarregar o
 * fluxo, abrir aba) aqui é estado: a pré-visualização por fluxo, os contadores de recarga e as
 * reações dos slices de agente e de abas à mesma action. Cada editor lê só o que é do seu fluxo.
 */

const emptyPreview = (): FlowBuilderFlowPreview => ({ nodes: [], connections: [] });

function upsertById<T extends { id: string }>(items: T[], item: T): boolean {
  const index = items.findIndex((current) => current.id === item.id);
  if (index === -1) {
    items.push(item);
    return true;
  }
  items[index] = item;
  return false;
}

function previewOf(agent: FlowBuilderAgentState, flowId: string): FlowBuilderFlowPreview {
  agent.previews[flowId] ??= emptyPreview();
  return agent.previews[flowId];
}

function startToolCall(turn: FlowBuilderTurn, event: FlowBuilderStreamEventOf<'tool_call'>) {
  turn.activity.toolCalls.push({
    callId: event.callId,
    tool: event.tool,
    status: 'running',
    summary: null,
  });
  turn.thinkingLabel = `${flowBuilderToolLabel(event.tool)}…`;
}

function settleToolCall(turn: FlowBuilderTurn, event: FlowBuilderStreamEventOf<'tool_result'>) {
  const settled = {
    callId: event.callId,
    tool: event.tool,
    status: event.success ? ('succeeded' as const) : ('failed' as const),
    summary: event.summary?.trim() || null,
  };
  const index = turn.activity.toolCalls.findIndex((call) => call.callId === event.callId);
  if (index === -1) turn.activity.toolCalls.push(settled);
  else turn.activity.toolCalls[index] = settled;
}

function groupOf(groups: FlowBuilderIssueGroup[], flowName: string): FlowBuilderIssueGroup {
  const existing = groups.find((group) => group.flowName === flowName);
  if (existing) return existing;
  const created: FlowBuilderIssueGroup = { flowName, issues: [] };
  groups.push(created);
  return created;
}

/**
 * Cada `validation_error` é uma rodada de reparo do mesmo fluxo: vale a última, não a soma — senão
 * o card listaria como pendente o que a rodada anterior já corrigiu.
 */
function recordRepairIssues(
  turn: FlowBuilderTurn,
  event: FlowBuilderStreamEventOf<'validation_error'>,
) {
  groupOf(turn.activity.repairIssues, event.flowName).issues = event.issues;
  turn.thinkingLabel = `Revisão pediu ${event.issues.length} ajuste(s). Corrigindo…`;
}

const sameIssue = (left: FlowBuilderValidationIssue, right: FlowBuilderValidationIssue) =>
  left.code === right.code && left.message === right.message && left.nodeId === right.nodeId;

/** O nome vem da issue quando ela o traz: um evento pode carregar avisos de mais de um fluxo. */
function recordGuidance(
  turn: FlowBuilderTurn,
  event: FlowBuilderStreamEventOf<'validation_warning'>,
) {
  for (const issue of event.issues) {
    const group = groupOf(turn.activity.guidance, issue.flowName ?? event.flowName);
    if (!group.issues.some((known) => sameIssue(known, issue))) group.issues.push(issue);
  }
  turn.thinkingLabel = `Revisão sugeriu ${event.issues.length} ajuste(s) opcionais`;
}

function addResource(turn: FlowBuilderTurn, resource: FlowBuilderCreatedResource, label: string) {
  const known = turn.activity.createdResources.some(
    (item) => item.kind === resource.kind && item.id === resource.id,
  );
  if (!known) turn.activity.createdResources.push(resource);
  turn.thinkingLabel = label;
}

function startDrawing(
  agent: FlowBuilderAgentState,
  turn: FlowBuilderTurn,
  event: FlowBuilderStreamEventOf<'flow_start'>,
) {
  agent.previews[event.flowId] = emptyPreview();
  turn.build = {
    drawingFlowId: event.flowId,
    drawingFlowName: event.name,
    createdNodes: 0,
    totalNodes: event.totalNodes,
  };
  turn.thinkingLabel = `Desenhando ${event.name}…`;
}

function addPreviewNode(
  agent: FlowBuilderAgentState,
  turn: FlowBuilderTurn,
  event: FlowBuilderStreamEventOf<'node_created'>,
) {
  const isNew = upsertById(previewOf(agent, event.flowId).nodes, event.node);
  if (isNew && turn.build.drawingFlowId === event.flowId) turn.build.createdNodes += 1;
}

/**
 * Fecha o desenho deste fluxo, não o turno. A pré-visualização fica até o editor do fluxo terminar
 * de recarregar do servidor (`flowBuilderPreviewCleared`): apagá-la aqui faria o canvas piscar no
 * estado antigo enquanto o GET não volta.
 */
function completeDrawing(
  agent: FlowBuilderAgentState,
  turn: FlowBuilderTurn,
  event: FlowBuilderStreamEventOf<'flow_complete'>,
) {
  const completed = turn.activity.completedFlows.filter((flow) => flow.flowId !== event.flowId);
  completed.push({
    flowId: event.flowId,
    name: event.name,
    nodeCount: event.nodeCount,
    connectionCount: event.connectionCount,
  });
  turn.activity.completedFlows = completed;
  if (turn.build.drawingFlowId === event.flowId) turn.build.drawingFlowId = null;
  agent.serverReloads.byFlowId[event.flowId] =
    (agent.serverReloads.byFlowId[event.flowId] ?? 0) + 1;
  turn.thinkingLabel = `${event.name} gravado no rascunho`;
}

/**
 * `task_completed` traz a revisão do rascunho depois do turno. Se ela mudou, o turno pode ter
 * mexido em fluxo sem `flow_complete` (patch, instruções): todo editor do agente recarrega.
 */
function completeTask(
  agent: FlowBuilderAgentState,
  turn: FlowBuilderTurn,
  event: FlowBuilderStreamEventOf<'task_completed'>,
) {
  turn.thinkingLabel = null;
  turn.build.drawingFlowId = null;
  if (event.draftRevision !== null && event.draftRevision !== turn.startRevision) {
    agent.serverReloads.agent += 1;
  }
}

const hasActivity = (activity: FlowBuilderTurnActivity) =>
  Object.values(activity).some((items: unknown[]) => items.length > 0);

/** Só a pré-visualização de fluxo que chegou a `flow_complete` foi gravada; o resto morre aqui. */
function discardUnsavedPreviews(agent: FlowBuilderAgentState, turn: FlowBuilderTurn) {
  const saved = new Set(turn.activity.completedFlows.map((flow) => flow.flowId));
  for (const flowId of Object.keys(agent.previews)) {
    if (!saved.has(flowId)) delete agent.previews[flowId];
  }
}

const belongsToOpenChat = (agent: FlowBuilderAgentState, turn: FlowBuilderTurn) =>
  turn.sessionId === null || agent.chat.sessionId === turn.sessionId;

/** A lista de sessões é uma foto: sem isto ela seguiria anunciando para reconexão um turno morto. */
function releaseLiveTurn(agent: FlowBuilderAgentState, turn: FlowBuilderTurn) {
  const session = agent.sessions.items.find((item) => item.id === turn.sessionId);
  if (session?.liveTurnId === turn.turnId) session.liveTurnId = null;
}

export function finishFlowBuilderTurn(
  agent: FlowBuilderAgentState,
  turn: FlowBuilderTurn,
  { content, interrupted }: { content: string; interrupted: boolean },
) {
  if ((content || hasActivity(turn.activity)) && belongsToOpenChat(agent, turn)) {
    agent.chat.messages.push({
      key: `turn:${turn.turnId}:assistant`,
      role: 'ASSISTANT',
      content,
      turnId: turn.turnId,
      createdAt: null,
      activity: turn.activity,
      ...(interrupted ? { interrupted } : {}),
    });
  }
  turn.phase = 'finished';
  turn.streamingText = '';
  turn.thinkingLabel = null;
  turn.build.drawingFlowId = null;
  discardUnsavedPreviews(agent, turn);
  releaseLiveTurn(agent, turn);
}

/** Com entrevista pendente o `assistantText` repete as perguntas, que o card já mostra. */
function assistantContentOf(
  agent: FlowBuilderAgentState,
  turn: FlowBuilderTurn,
  assistantText: string | null,
): string {
  const streamed = turn.streamingText.trim();
  if (streamed) return streamed;
  if (agent.pendingInterview?.turnId === turn.turnId) return '';
  return assistantText?.trim() ?? '';
}

function failTurn(
  agent: FlowBuilderAgentState,
  turn: FlowBuilderTurn,
  event: FlowBuilderStreamEventOf<'error'>,
) {
  turn.failure = {
    source: 'turn',
    code: event.code,
    message: event.message,
    recoverable: event.recoverable,
  };
  finishFlowBuilderTurn(agent, turn, { content: turn.streamingText.trim(), interrupted: true });
}

function askQuestions(
  agent: FlowBuilderAgentState,
  turn: FlowBuilderTurn,
  event: FlowBuilderStreamEventOf<'interview_question'>,
) {
  if (event.questions.length === 0) return;
  agent.pendingInterview = {
    turnId: turn.turnId,
    questions: event.questions,
    introText: event.introText ?? null,
  };
}

/** Exaustividade em tempo de compilação; em execução, o tipo novo do core só é ignorado. */
function ignoreUnknownEvent(_event: never): void {
  // Política aditiva do contrato: evento que este cliente não conhece não muda o estado.
}

function reduceTurnEvent(
  agent: FlowBuilderAgentState,
  turn: FlowBuilderTurn,
  event: FlowBuilderStreamEvent,
): void {
  switch (event.type) {
    case 'turn_queued':
      turn.sessionId ??= event.sessionId;
      turn.thinkingLabel = 'Na fila…';
      break;
    case 'task_started':
      turn.sessionId ??= event.sessionId;
      turn.thinkingLabel = 'Começando…';
      break;
    case 'thinking':
      turn.thinkingLabel = humanizeFlowBuilderStatus(event.status) ?? turn.thinkingLabel;
      break;
    case 'text':
      turn.streamingText += event.content;
      break;
    case 'tool_call':
      startToolCall(turn, event);
      break;
    case 'tool_result':
      settleToolCall(turn, event);
      break;
    case 'validation_error':
      recordRepairIssues(turn, event);
      break;
    case 'validation_warning':
      recordGuidance(turn, event);
      break;
    case 'artifact_created':
      turn.thinkingLabel = 'Preparando o resultado…';
      break;
    case 'custom_field_created':
      addResource(
        turn,
        { kind: 'field', id: event.id, label: event.key, detail: event.fieldType },
        'Criando campo personalizado…',
      );
      break;
    case 'custom_function_created':
      addResource(
        turn,
        { kind: 'function', id: event.id, label: event.name, detail: null },
        'Criando função personalizada…',
      );
      break;
    case 'sub_flow_created':
      addResource(
        turn,
        { kind: 'subFlow', id: event.flowId, label: event.name, detail: event.functionName },
        'Gerando sub-fluxo…',
      );
      break;
    case 'flow_start':
      startDrawing(agent, turn, event);
      break;
    case 'node_created':
      addPreviewNode(agent, turn, event);
      break;
    case 'connection_created':
      upsertById(previewOf(agent, event.flowId).connections, event.connection);
      break;
    case 'flow_complete':
      completeDrawing(agent, turn, event);
      break;
    case 'draft_updated':
      // A revisão nova mora no slice do agente, que reage a esta mesma action.
      break;
    case 'draft_conflict':
      turn.draftConflict = {
        expectedRevision: event.expectedRevision,
        currentRevision: event.currentRevision,
      };
      break;
    case 'interview_question':
      askQuestions(agent, turn, event);
      break;
    case 'simulation_result':
      turn.activity.simulations.push({
        flowId: event.flowId,
        simulacaoId: event.simulacaoId,
        turns: event.turns,
        handedOffToHuman: event.handedOffToHuman,
        ended: event.ended,
        truncated: event.truncated,
      });
      turn.thinkingLabel = 'Conversa de teste concluída';
      break;
    case 'task_completed':
      completeTask(agent, turn, event);
      break;
    case 'error':
      failTurn(agent, turn, event);
      break;
    case 'done':
      finishFlowBuilderTurn(agent, turn, {
        content: assistantContentOf(agent, turn, event.assistantText),
        interrupted: false,
      });
      break;
    default:
      ignoreUnknownEvent(event);
  }
}

/**
 * Aplica um evento do stream ao estado do agente. Evento de outro turno (o anterior, ou um que
 * chega depois de o turno terminar) é descartado: é o que impede a geração antiga de pintar o
 * canvas depois de o usuário ter mandado outra mensagem.
 */
export function applyFlowBuilderStreamEvent(
  agent: FlowBuilderAgentState,
  { turnId, eventId, event }: FlowBuilderStreamEventReceivedPayload,
): void {
  const turn = agent.turn;
  if (!turn || turn.turnId !== turnId || turn.phase === 'finished') return;
  if (eventId) turn.lastEventId = eventId;
  reduceTurnEvent(agent, turn, event);
  // Turno retomado que chegou ao fim pelo stream: o próprio core confirmou, o editor volta a salvar.
  // A recarga vai junto porque stream expirado entrega só o terminal, sem os `flow_complete` e
  // `task_completed` que a pediriam.
  if (isFlowBuilderTerminalEvent(event) && agent.unconfirmedTurn?.turnId === turnId) {
    agent.unconfirmedTurn = null;
    agent.serverReloads.agent += 1;
  }
}
